import { signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";

/** Openable from anywhere (footer, count bar) — module-level signal rather than lifted
 *  component state, matching how the rest of the app shares UI state. */
const aboutOpen = signal(false);
export function openAbout() {
  aboutOpen.value = true;
}

/** Tiny static About modal: data provenance, privacy posture, contact. A native <dialog>
 *  gives focus trapping and Escape for free; clicking the backdrop closes it too. */
export function AboutDialog() {
  const open = aboutOpen.value;
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    else if (!open && dlg.open) dlg.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      class="about-dialog"
      aria-label="About the Classical Concert Map"
      onClose={() => {
        aboutOpen.value = false;
      }}
      onClick={(e) => {
        // Only the backdrop is the <dialog> element itself; clicks on content land on
        // children.
        if (e.target === ref.current) ref.current?.close();
      }}
    >
      <h2>About</h2>
      <p>
        All concert data is collected from public, first-party sources, not aggregators.
      </p>
      <p>
        The Classical Concert Map does not use cookies and collects no personal data,
        apart from public IP addresses, which may be stored for security purposes.
      </p>
      <p>
        Researchers: every list can be downloaded as a spreadsheet (Download CSV, above the
        list), and the data is available through a public API —{" "}
        <a href="/api-docs/">see the Data &amp; API page</a>.
      </p>
      <p>
        If you have questions about the project, please email{" "}
        <a href="mailto:loft4map@gmail.com">loft4map@gmail.com</a>.
      </p>
      <p>
        If you value the concert map, please consider{" "}
        <a href="https://thecot.org/support/" target="_blank" rel="noopener noreferrer">
          making a donation to the Chamber Orchestra of the Triangle
        </a>{" "}
        (not affiliated with us; just our favorite orchestra).
      </p>
      <p class="about-credits">
        Reference data courtesy of{" "}
        <a href="https://geonames.org" target="_blank" rel="noopener noreferrer">
          GeoNames
        </a>{" "}
        (CC&nbsp;BY&nbsp;4.0),{" "}
        <a href="https://openopus.org" target="_blank" rel="noopener noreferrer">
          Open Opus
        </a>
        ,{" "}
        <a href="https://musicbrainz.org" target="_blank" rel="noopener noreferrer">
          MusicBrainz
        </a>
        ,{" "}
        <a href="https://www.wikidata.org" target="_blank" rel="noopener noreferrer">
          Wikidata
        </a>
        , and the{" "}
        <a
          href="https://data.carnegiehall.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          Carnegie Hall Data Lab
        </a>
        .
      </p>
      <p class="about-rights">All rights reserved.</p>
      <button type="button" class="about-close" onClick={() => ref.current?.close()}>
        Close
      </button>
    </dialog>
  );
}
